import { useEffect, useState } from "react";
import { Phone } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";
import { useBreadcrumbs } from "@/hooks/useBreadcrumbs";
import { openNewYearBooking, type NewYearBookingSource } from "@/components/booking-modal";
import {
  isCampaignActive, NY_MENU, NY_PHONE, NY_PHONE_HREF, NY_POSTER, NY_PROGRAM, NY_START_ISO,
} from "@/components/new-year/campaign";

export default function NovyyGod2027() {
  useSeo({
    title: "Императорский Новый год 2027 на крыше — Паруса на крыше, Петербург",
    description: "31 декабря 2026, начало 22:00. Императорский бал, торжественная встреча, Дед Мороз и Снегурочка, диджеи и императорское меню. Ул. Марата, 5. Бронирование: +7 (812) 991-10-90.",
    keywords: "новый год 2027 ресторан Петербург, новогодняя ночь на крыше СПб, императорский новый год, Паруса на крыше новый год",
    image: NY_POSTER,
    canonical: "/novyy-god-2027",
  });
  useBreadcrumbs([{ name: "Новый год 2027", path: "/novyy-god-2027" }]);

  // Date-gated copy resolved after hydration to keep SSR markup stable
  const [archived, setArchived] = useState(false);
  useEffect(() => setArchived(!isCampaignActive()), []);

  return (
    <div className="w-full bg-background">
      {/* HERO */}
      <section className="ny-stage relative overflow-hidden pt-[110px] md:pt-[140px] pb-20">
        <div className="ny-curtain" aria-hidden="true" />
        <div className="relative z-10 container mx-auto px-6 md:px-14 grid lg:grid-cols-[1.1fr_1fr] gap-12 items-center">
          <div className="ny-reveal">
            <p className="text-[9px] tracking-[0.4em] uppercase text-[hsl(var(--ny-gold))] mb-6">Встречаем 2027 · Паруса на крыше</p>
            <h1 className="font-serif font-light text-white leading-[1.02] text-[clamp(36px,5.4vw,82px)] mb-8">
              Императорский<br /><span className="italic text-[hsl(var(--ny-gold))]">Новый год</span>
            </h1>
            <dl className="grid grid-cols-2 gap-px bg-[hsl(var(--ny-gold)/0.25)] border border-[hsl(var(--ny-gold)/0.25)] max-w-md mb-8">
              {[["Дата", "31 декабря 2026"], ["Начало", "22:00"], ["Место", "Паруса на крыше"], ["Адрес", "ул. Марата, 5"]].map(([k, v]) => (
                <div key={k} className="bg-[hsl(var(--background))] p-4">
                  <dt className="text-[8px] tracking-[0.3em] uppercase text-white/40 mb-1">{k}</dt>
                  <dd className="text-white text-sm">{v}</dd>
                </div>
              ))}
            </dl>
            <p className="text-white/70 leading-[1.9] max-w-lg mb-10">
              В эту ночь ресторан становится императорским дворцом над Петербургом: церемониальный приём,
              бал и щедрый праздничный стол — до самого утра.
            </p>
            {archived ? (
              <p className="text-white/50 text-sm border-l-2 border-[hsl(var(--ny-gold))] pl-4">Событие состоялось. Спасибо, что встретили 2027 вместе с нами.</p>
            ) : (
              <BookingActions source="new_year_page_hero" />
            )}
          </div>
          <figure className="ny-poster-frame mx-auto w-full max-w-[480px]">
            <img src={NY_POSTER} alt="Афиша Императорского Нового года 2027 в ресторане Паруса на крыше" fetchPriority="high" decoding="async" className="w-full h-auto object-contain block" />
          </figure>
        </div>
      </section>

      {/* PROGRAM */}
      <section aria-labelledby="ny-program" className="py-20 md:py-28 border-t border-primary/15">
        <div className="container mx-auto px-6 md:px-14 max-w-5xl">
          <div>
            <p className="text-[9px] tracking-[0.35em] uppercase text-primary mb-3">Акт за актом</p>
            <h2 id="ny-program" className="font-serif text-4xl md:text-5xl font-light text-foreground mb-12">Программа вечера</h2>
            <ol className="border-l border-[hsl(var(--ny-gold)/0.35)]">
              {NY_PROGRAM.map((p, i) => (
                <li key={p.t} className="relative pl-8 md:pl-12 pb-9 last:pb-0">
                  <span className="absolute -left-[5px] top-2 w-[9px] h-[9px] rotate-45 bg-[hsl(var(--ny-gold))]" aria-hidden="true" />
                  <span className="font-serif text-sm text-[hsl(var(--ny-gold))] italic">{["I","II","III","IV","V","VI","VII"][i]}</span>
                  <h3 className="font-serif text-2xl md:text-3xl font-light text-foreground mt-1">{p.t}</h3>
                  <p className="text-muted-foreground text-sm mt-2 max-w-lg">{p.d}</p>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* MENU */}
      <section aria-labelledby="ny-menu" className="ny-stage relative py-20 md:py-28 border-t border-[hsl(var(--ny-gold)/0.3)]">
        <div className="relative z-10 container mx-auto px-6 md:px-14 max-w-3xl text-center">
          <p className="text-[9px] tracking-[0.35em] uppercase text-[hsl(var(--ny-gold))] mb-3">Включено в стоимость билета</p>
          <h2 id="ny-menu" className="font-serif text-4xl md:text-6xl font-light text-white mb-14">Императорское меню</h2>
          <ul>
            {NY_MENU.map((m) => (
              <li key={m.course} className="py-7 border-t border-[hsl(var(--ny-gold)/0.2)] last:border-b">
                <div className="text-[9px] tracking-[0.35em] uppercase text-[hsl(var(--ny-gold))] mb-2">{m.course}</div>
                <div className="font-serif text-xl md:text-2xl font-light text-white italic">{m.dish}</div>
              </li>
            ))}
          </ul>
          <p className="text-white/50 text-sm mt-10">Стоимость и наличие мест уточняйте при бронировании.</p>
        </div>
      </section>

      {/* FINAL CTA */}
      {!archived && (
        <section className="py-20 border-t border-primary/15">
          <div className="container mx-auto px-6 md:px-14 flex flex-col items-center text-center">
            <h2 className="font-serif text-4xl md:text-5xl font-light text-foreground mb-4">Ваше место на балу</h2>
            <p className="text-muted-foreground mb-10 max-w-md">Ул. Марата, 5. Забронируйте онлайн или позвоните — расскажем о стоимости и свободных местах.</p>
            <BookingActions center source="new_year_page_footer" />
          </div>
        </section>
      )}

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(EVENT_SCHEMA) }} />
    </div>
  );
}

function BookingActions({ center = false, source }: { center?: boolean; source: NewYearBookingSource }) {
  return (
    <div className={`flex flex-col sm:flex-row gap-3 ${center ? "justify-center" : ""}`}>
      <button onClick={() => openNewYearBooking(source)} data-testid="button-ny-book" className="ny-btn-gold">Забронировать стол</button>
      <a href={NY_PHONE_HREF} data-testid="link-ny-phone" className="ny-btn-ghost inline-flex items-center justify-center gap-2">
        <Phone className="w-3.5 h-3.5" aria-hidden="true" /> {NY_PHONE}
      </a>
    </div>
  );
}

const EVENT_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "Event",
  name: "Императорский Новый год 2027",
  description: "Новогодняя ночь в ресторане Паруса на крыше: императорский бал, торжественная встреча, праздничное застолье, Дед Мороз и Снегурочка, караоке, диджеи и императорское меню.",
  startDate: NY_START_ISO,
  eventStatus: "https://schema.org/EventScheduled",
  eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
  image: `https://parusanakryshe.ru${NY_POSTER}`,
  url: "https://parusanakryshe.ru/novyy-god-2027",
  location: {
    "@type": "Place",
    name: "Паруса на крыше",
    telephone: "+78129911090",
    address: { "@type": "PostalAddress", streetAddress: "ул. Марата, 5", addressLocality: "Санкт-Петербург", addressCountry: "RU" },
  },
  organizer: { "@type": "Organization", name: "Паруса на крыше", url: "https://parusanakryshe.ru" },
};
